// Surface 1 — every product ranked by clicks lost.
//
// No search input and nothing to configure: a seller opening this does not yet
// know which product has a problem, and finding out is the job.
import React, { useState } from 'react';
import s from '../CTROptimizer.module.css';
import { num, pct } from '../format';

const SORTS = [
  ['clicks_lost', 'Clicks lost'], ['gap', 'Gap to benchmark'],
  ['search_click_rate', 'Click rate'], ['impressions', 'Impressions'],
];

export default function ProductList({ data, onOpen, rollUp, onRollUp }) {
  const [sort, setSort] = useState('clicks_lost');
  const [byType, setByType] = useState(true);
  if (!data) return <div className={s.empty}>Loading…</div>;
  if (!data.products?.length) {
    return <div className={s.empty}>No products with search data for this window.</div>;
  }

  const rankable = data.products.filter((p) => p.rankable);
  const suppressed = data.products.filter((p) => !p.rankable);
  const sorted = [...rankable].sort((a, b) => (b[sort] ?? 0) - (a[sort] ?? 0));

  // Baseline click rates differ enormously between a cheap accessory and a
  // large appliance; ranking across the whole catalogue surfaces the cheapest
  // products every time and tells the seller nothing.
  const groups = byType
    ? sorted.reduce((acc, p) => {
        const key = p.product_type || 'No product type';
        (acc[key] = acc[key] || []).push(p);
        return acc;
      }, {})
    : { 'All products': sorted };

  return (
    <>
      <div className={s.controls} style={{ marginBottom: 12 }}>
        <label className={s.field}>
          <span className={s.label}>Sort by</span>
          <select className={s.select} value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className={s.field} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={byType} onChange={(e) => setByType(e.target.checked)} />
          <span className={s.label} style={{ margin: 0 }}>Group by product type</span>
        </label>
        <label className={s.field} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={rollUp} onChange={(e) => onRollUp(e.target.checked)} />
          <span className={s.label} style={{ margin: 0 }}>Roll children into parents</span>
        </label>
        <span className={s.hint}>
          Ranked by clicks lost, not by rate — a rate ranking puts the smallest products on
          top and hides the ones losing real traffic. Movement is against four windows
          earlier; a parent&apos;s figures are its children&apos;s counts summed, never the
          average of their rates.
        </span>
      </div>

      {Object.entries(groups).map(([kind, rows]) => (
        <div key={kind} className={s.section}>
          {byType && <div className={s.sectionTitle}>{kind} <span className={s.muted}>
            · {rows.length} products</span></div>}
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th className={s.numeric}>Impressions</th>
                  <th className={s.numeric}>Search CTR</th>
                  <th className={s.numeric}>Benchmark</th>
                  <th className={s.numeric}>Gap</th>
                  <th className={s.numeric}>Clicks lost</th>
                  <th className={s.numeric}>Moved</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.asin}>
                    <td>
                      <button className={s.term} onClick={() => onOpen(p.asin)}>{p.asin}</button>
                    </td>
                    <td className={s.numeric}>{num(p.impressions)}</td>
                    <td className={s.numeric}>{pct(p.search_click_rate)}</td>
                    <td className={s.numeric}>
                      {pct(p.benchmark)}
                      <span className={s.benchTag}>{p.benchmark_source}</span>
                    </td>
                    <td className={s.numeric}>
                      <span className={(p.gap ?? 0) > 0 ? s.down : s.up}>
                        {p.gap == null ? '—' : `${(p.gap * 100).toFixed(0)}%`}
                      </span>
                    </td>
                    <td className={s.numeric}><strong>{num(p.clicks_lost)}</strong></td>
                    <td className={s.numeric}>
                      {p.movement == null
                        ? <span className={s.muted}>—</span>
                        : <span className={p.movement >= 0 ? s.up : s.down}>
                            {p.movement >= 0 ? '+' : ''}{(p.movement * 100).toFixed(2)}pp
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {!!suppressed.length && (
        <div className={s.section}>
          <div className={s.sectionTitle}>Not ranked ({suppressed.length})</div>
          <p className={s.hint}>
            These keep their counts and lose their rate. A click rate built on a few hundred
            impressions moves enough on chance alone to send you after a problem that is
            not there.
          </p>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Product</th><th className={s.numeric}>Impressions</th>
                  <th className={s.numeric}>Clicks</th><th>Why not ranked</th>
                </tr>
              </thead>
              <tbody>
                {suppressed.map((p) => (
                  <tr key={p.asin}>
                    <td className={s.asin}>{p.asin}</td>
                    <td className={s.numeric}>{num(p.impressions)}</td>
                    <td className={s.numeric}>{num(p.clicks)}</td>
                    <td className={s.suppressedRow}>{p.suppressed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
